import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Loader2 } from "lucide-react";

// Bridge page for Stripe → Mobile deep-link handoff.
// Stripe cannot accept custom-scheme URLs (e.g. `nrimatrimony://`) as success_url,
// so mobile-originated checkouts redirect here first. This page immediately
// invokes the app's deep link, which `WebBrowser.openAuthSessionAsync` on the
// device intercepts and closes — control returns to the mobile app.
// Deliberately no auth, no data-fetching — must work in an anonymous browser tab.
export default function MobileBridge() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id") || "";
  const cancelled = params.get("cancelled") === "1";
  const [triggered, setTriggered] = useState(false);

  useEffect(() => {
    const deepLink = cancelled
      ? "nrimatrimony://payment/cancel"
      : `nrimatrimony://payment/success?session_id=${encodeURIComponent(sessionId)}`;
    // Trigger deep link. On Android this shows the "Open with" prompt momentarily
    // or launches straight into the app if it's the registered handler.
    const t = setTimeout(() => {
      window.location.replace(deepLink);
      setTriggered(true);
    }, 250);
    return () => clearTimeout(t);
  }, [sessionId, cancelled]);

  return (
    <div className="min-h-screen flex items-center justify-center px-4 bg-[#FDFBF7]" data-testid="mobile-bridge">
      <Card className="p-10 max-w-md w-full text-center border-[#EAE3D2] bg-white">
        <Loader2 className="w-14 h-14 mx-auto text-[#6B1D2F] animate-spin" />
        <h1 className="font-serif text-2xl text-slate-900 mt-4">
          {cancelled ? "Returning to EliteNRIMatrimony…" : "Payment received — returning to app…"}
        </h1>
        <p className="text-slate-600 mt-2 text-sm">
          If the app doesn't reopen automatically,{" "}
          <a
            data-testid="mobile-bridge-manual-link"
            href={cancelled ? "nrimatrimony://payment/cancel" : `nrimatrimony://payment/success?session_id=${encodeURIComponent(sessionId)}`}
            className="text-[#6B1D2F] font-semibold underline"
          >
            tap here to open EliteNRIMatrimony
          </a>
          .
        </p>
        {triggered && (
          <p className="text-slate-400 text-xs mt-6">
            You can now close this tab.
          </p>
        )}
      </Card>
    </div>
  );
}
